import { useEffect, useState, useMemo, useCallback } from 'preact/hooks'
import { PURCHASE_OPTIONS } from 'helpers/constants'
import { formatMoney, getPriceSubscription, getSubscriptionSave } from 'libs/shopify'
import { emitterProps, globalEmitter } from 'helpers/emitter-event'

function useSubscriptionPlans(planGroups, currentVariant, isBundleDynamic = false) {
  const availablePlanGroups = useMemo(() => {
    if (!planGroups?.length || !currentVariant?.sellingPlanAllocations) return []

    return planGroups
      .filter(group =>
        currentVariant.sellingPlanAllocations.some(
          allocation => allocation?.sellingPlanGroupId === group.id
        )
      )
      .map(group => {
        const price = getPriceSubscription(currentVariant, group.id, currentVariant.price)
        const formatPrice = formatMoney(price)
        const name = isBundleDynamic ? group.name : `${group.name} - ${formatPrice}`

        return {
          ...group,
          name,
          value: group.id
        }
      })
  }, [planGroups, currentVariant])

  const [selectedPlan, setSelectedPlan] = useState(null)

  useEffect(() => {
    if (availablePlanGroups.length && !selectedPlan) {
      setSelectedPlan(availablePlanGroups[0])
    } else if (!availablePlanGroups.length) {
      setSelectedPlan(null)
    }
  }, [availablePlanGroups, selectedPlan])

  return {
    availablePlanGroups,
    selectedPlan,
    setSelectedPlan,
    hasAvailablePlans: availablePlanGroups.length > 0
  }
}

function usePurchaseOptions(currentVariant, selectedPlan, setSellingPlanId, setCurrentVariantPrice) {
  const [selectedPurchaseOption, setSelectedPurchaseOption] = useState(PURCHASE_OPTIONS.ONE_TIME)
  const isSubscriptionSelected = useMemo(() => selectedPurchaseOption === PURCHASE_OPTIONS.SUBSCRIPTION, [selectedPurchaseOption])

  const subscriptionPrice = useMemo(() => {
    if (!selectedPlan) return currentVariant.price

    return getPriceSubscription(currentVariant, selectedPlan.id, currentVariant.price)
  }, [currentVariant, selectedPlan])

  const subscriptionSavings = useMemo(() => {
    if (!selectedPlan) return ''

    return getSubscriptionSave([selectedPlan], selectedPlan.id)
  }, [selectedPlan])

  useEffect(() => {
    const isValidPlan = selectedPlan?.selling_plans?.[0]?.id &&
      currentVariant?.sellingPlanAllocations?.some(
        allocation => allocation?.sellingPlanGroupId === selectedPlan.id
      )

    const newSellingPlanId = isSubscriptionSelected && isValidPlan ? selectedPlan.selling_plans[0].id : null

    setSellingPlanId(newSellingPlanId)
    globalEmitter.emit(emitterProps.product.updateSellingPlan, newSellingPlanId)
  }, [selectedPurchaseOption, currentVariant, selectedPlan])

  useEffect(() => {
    const price = isSubscriptionSelected ? subscriptionPrice : currentVariant.price

    setCurrentVariantPrice(price)
    globalEmitter.emit(emitterProps.product.updatePrice, price)
  }, [selectedPurchaseOption, subscriptionPrice, currentVariant])

  const handlePurchaseOptionChange = useCallback((option, isChecked) => {
    if (isChecked) {
      setSelectedPurchaseOption(option)
    } else {
      setSelectedPurchaseOption(PURCHASE_OPTIONS.ONE_TIME)
    }
  }, [])

  return {
    selectedPurchaseOption,
    subscriptionPrice,
    subscriptionSavings,
    handlePurchaseOptionChange,
    isSubscriptionSelected
  }
}

export { useSubscriptionPlans, usePurchaseOptions }
